import { add, format, formatDistance, isValid } from "date-fns";
import _, { camelCase, kebabCase, mapKeys } from "lodash";

// Builds an object that has the same nested attributes as the given
// comma-separated string `path`
export const buildFromPath = (path, val = {}) => {
  let pointer;

  const obj = path.split(".").reduce((obj, seg, i, a) => {
    const isLast = i === a.length - 1;
    if (!pointer) pointer = obj;
    pointer[seg] = isLast ? val : {};
    pointer = pointer[seg];
    return obj;
  }, {});
  return obj;
};

export const kebabCaseKeys = (obj) => mapKeys(obj, (_, k) => kebabCase(k));
export const camelCaseKeys = (obj) => mapKeys(obj, (_, k) => camelCase(k));

export const kebabCaseKeysDeep = (obj) => {
  if (!_.isObjectLike(obj)) return obj;
  if (Array.isArray(obj)) return obj.map(kebabCaseKeysDeep);
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      kebabCase(key),
      typeof value === "object" ? kebabCaseKeysDeep(value) : value,
    ])
  );
};

// A simple wait function that returns a promise to be used when setting timers
export const wait = async (ms) => {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
};

export const upCase = (string, splitter = " ") => {
  if (!string) return string;
  return string
    .trim()
    .split(splitter)
    .map((s) => s[0].toUpperCase() + s.slice(1))
    .join(" ");
};

/*
Takes in the current filters from the filterSlice, and generates a query object that
is stored in the url of each view, which is then used to fetch data based on the url
if the user navigates with the back or forward keys, or shares a link with a colleague.
*/
export const buildQueryObject = (filters) => {
  const arrayFilters = [
    "groupIds",
    "territoryIds",
    "channelIds",
    "programIds",
    "userIds",
    "stateIds",
    "budgetGroupIds",
    "favoriteItems",
    "orderWindowIds",
    "promotionIds",
    "purchaserIds",
    "supplierIds",
  ];

  const ignoreKeys = [
    "chipList",
    "defaultFilters",
    "clearFilters",
    "clearSku",
    "clearName",
    "clearOrderId",
    "sorted",
    "currentTerritoryId",
    "orderWindowId",
    "useOrderWindowTerritoryId",
    "channelId",
  ];
  const strippedFilters = Object.keys(filters).reduce((acc, filter) => {
    if (!ignoreKeys.includes(filter)) {
      if (
        (arrayFilters.includes(filter) && filters[filter].length > 0) ||
        (!arrayFilters.includes(filter) && filters[filter])
      ) {
        acc[filter] = filters[filter];
      }
    }
    return acc;
  }, {});

  return strippedFilters;
};

const intlFormater = new Intl.NumberFormat("en-US");
export const formatNumber = intlFormater.format;

const USDollar = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

export const formatMoney = (value, ops) =>
  formatMoneyCents(isNaN(value) ? 0 : value, ops ? 4 : 2);

export const formatMoneyCents = (value: number, decimals = 2) =>
  `$${parseFloat((value / 100).toString()).toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;

export const formatMoneyString = (value) => USDollar.format(value ?? 0);

//handles incoming money values that are strings
export const stringToCents = (numString) => {
  return parseFloat(numString) * 100;
};

export const limitTwoDecimals = (value) => {
  return Number(value).toLocaleString("fullwide", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

//Used in order tables to ensure order numbers match pack size
export const roundUp = (value, rounder) => {
  if (rounder === 0) return value;
  return Math.ceil(value / rounder) * rounder;
};

export const roundDown = (value, rounder) => {
  if (rounder < 2) return value;
  return Math.floor(value / rounder) * rounder;
};

//Used for filtering purposes, just a quick call to modify an array
export const separateByComma = (filter, arg, key) => {
  if (typeof arg !== "object") {
    return `filter[${filter}]%5B%5D=${arg}`;
  } else {
    if (key && typeof arg[0] === "object") {
      return arg
        .map((index) => `filter[${filter}]%5B%5D=${index[key]}`)
        .join("&");
    } else {
      return arg.map((a) => `filter[${filter}]%5B%5D=${a}`).join("&");
    }
  }
};

// Since we don't save the time zone in the database, new Date() will
// try to use the local time zone, which is not what we want.
export const utcDate = (dateOrDateString) => {
  const date = new Date(dateOrDateString);
  const hourOffset = date.getTimezoneOffset() / 60;
  return add(date, { hours: hourOffset });
};

export const formatUtcDate = (
  dateOrDateString,
  formatString = "MM/dd/yyyy"
) => {
  if (!dateOrDateString) return "";
  const date = utcDate(dateOrDateString);
  if (!isValid(date)) return date.toString();
  return format(date, formatString);
};

export const formatDistanceFromNow = (date) =>
  formatDistance(date, new Date(), {
    addSuffix: true,
  });

//handles formatting dates coming from the api to a MM/DD/YYYY format
export const formatDateString = (date) => {
  if (typeof date !== "string") return date;
  const [a, b, c] = date.split("-");
  return [b, c, a].join("/");
};

export const chunkArray = (ar: any[], chunk: number) => {
  const newAr: any[] = [];
  const length = ar.length;

  if (chunk >= length || chunk === 0) {
    return ar;
  }

  for (let i = 0; i < length; i += chunk) {
    const tempAr = ar.slice(i, i + chunk);
    newAr.push(tempAr);
  }

  return newAr;
};

// Does some calculations to return a formated order total.
export const handleOrderGrandTotal = (order, organization) => {
  const {
    includeShippingInBudgets,
    includeTaxesInBudget,
    includeShippingInStripeCharge,
    includeTaxesInStripeCharge,
  } = organization;

  let freight;
  let tax;
  let total;

  switch (order.checkoutType) {
    case "budget":
      if (order.actFreight !== 0) {
        freight = includeShippingInBudgets ? order.actFreight : 0;
      } else {
        freight = includeShippingInBudgets ? order.estFreight : 0;
      }

      tax = includeTaxesInBudget ? order.estTax : 0;

      total = order.totalPrice + freight + tax;
      break;
    case "stripe":
      if (order.actFreight !== 0) {
        freight = includeShippingInStripeCharge ? order.actFreight : 0;
      } else {
        freight = includeShippingInStripeCharge ? order.estFreight : 0;
      }

      tax = includeTaxesInStripeCharge ? order.estTax : 0;

      total = order.totalPrice + freight + tax;
      break;
    default:
      freight = order.actFreight !== 0 ? order.actFreight : order.estFreight;
      total = order.totalPrice + freight + order.estTax;
  }

  return formatMoney(total, false);
};

export const flattenObject = (input, keyName?: string) => {
  const result = {};
  for (const key in input) {
    const newKey = keyName ? `${keyName}.${key}` : key;
    if (typeof input[key] === "object" && !Array.isArray(input[key])) {
      Object.assign(result, flattenObject(input[key], newKey));
    } else {
      result[newKey] = input[key];
    }
  }
  return result;
};
